import { router, useGlobalSearchParams } from 'expo-router';
import { useCallback } from 'react';

import { routes } from '@/constants/routes';
import { isRentalShop } from '@/utils/shop/isRentalShop';

import type { UseAddAppointmentParams } from './types';

/**
 * Adds an empty appointment to the cart and opens the step that fills it:
 * Choose a professional, or the services of the same professional on a rental
 * shop. Forwards the current search params except the per-screen ones.
 */
export function useAddAppointment({
  shop,
  cartId,
  barberIdOrRoute,
  barberId,
  addEmptyCartItem,
}: UseAddAppointmentParams) {
  const params = useGlobalSearchParams();

  return useCallback(() => {
    if (!shop.id) return;

    // On web, history.replaceState does not update useGlobalSearchParams, so
    // preselect is still here and would preselect on the next appointment.
    const {
      preselect: _preselect,
      isAddedAppointment: _isAddedAppointment,
      groupId: _groupId,
      categoryId: _categoryId,
      ...forwardParams
    } = params;
    // Rental shops book every group appointment with the same professional,
    // matching water-on-web.
    if (isRentalShop(shop.kind) && barberIdOrRoute) {
      addEmptyCartItem(barberId);
      router.push(
        routes.chooseService({
          ...forwardParams,
          shopRoute: shop.route,
          barberId: barberIdOrRoute,
          cart: cartId,
          isAddedAppointment: true,
        }),
      );
      return;
    }
    addEmptyCartItem();
    router.push(
      routes.chooseProfessional({
        ...forwardParams,
        shopRoute: shop.route,
        cart: cartId,
      }),
    );
  }, [
    addEmptyCartItem,
    shop.id,
    shop.route,
    shop.kind,
    params,
    cartId,
    barberIdOrRoute,
    barberId,
  ]);
}
